import {
  fetchData,
  setFileUploadOptions,
  setHeaderOptions,
} from "./apiHelpers";
import {
  MedicalRecordContactDetail,
  MedicalRecordRequest,
} from "./medical-record-requests.type";

const BASE_ROUTE = "/medical-record-requests";

export async function getMedicalRecordRequests(): Promise<
  MedicalRecordRequest[]
> {
  const request = setHeaderOptions({ method: "GET" });
  return fetchData<MedicalRecordRequest[]>(BASE_ROUTE, request);
}

export async function getMedicalRecordRequest(
  id: string
): Promise<MedicalRecordRequest> {
  if (!id) {
    throw new Error("medical record request id is required");
  }

  const options = setHeaderOptions({ method: "GET" });
  const res = await fetchData<MedicalRecordRequest>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    options
  );

  return res;
}

export async function sendMedicalRecordRequestReady(
  id: string
): Promise<MedicalRecordRequest> {
  if (!id) throw new Error("medical record request id is required");
  const options = setHeaderOptions({ method: "PUT" });
  return fetchData<MedicalRecordRequest>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}/send`,
    options
  );
}

export async function markMedicalRecordRequestReadyToSend(
  id: string
): Promise<MedicalRecordRequest> {
  if (!id) throw new Error("medical record request id is required");
  const options = setHeaderOptions({ method: "POST" });
  return fetchData<MedicalRecordRequest>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}/ready-to-send`,
    options
  );
}

export type UpdateMedicalRecordRequestPayload = Partial<{
  status:
    | "pending"
    | "ready_to_review"
    | "ready_to_send"
    | "lookup_in_progress"
    | "lookup_failed"
    | "no_fax_found"
    | "fax_sending"
    | "fax_sent"
    | "fax_failed"
    | "completed"
    | "department_not_attempted"
    | "department_contacted"
    | "department_contact_attempted"
    | "department_confirmed_receipt"
    | "department_confirmed_processed"
    | "department_rejected"
    | "canceled"
    | string;
  notes: string | null;
  actions: Record<string, unknown> | null;
  last_error: string | null;
  delivery_method: string | null;
  fax_number: string | null;
  email_address: string | null;
  address_text: string | null;
  recipient_name: string | null;
  recipient_type: string | null;
  send_started_at: string | null;
  send_finished_at: string | null;
  lookup_started_at: string | null;
  lookup_finished_at: string | null;
  attempt_count: number | null;
  form_values: Record<string, unknown> | null;
  recipientName: string | null;
  recipientType: string | null;
  recipientExternalId: string | null;
  deliveryMethod: string | null;
  faxNumber: string | null;
  emailAddress: string | null;
  contactDetails: MedicalRecordContactDetail[] | null;
}>;

export async function updateMedicalRecordRequest(
  id: string,
  payload: UpdateMedicalRecordRequestPayload
): Promise<MedicalRecordRequest> {
  if (!id) {
    throw new Error("medical record request id is required");
  }

  const options = setHeaderOptions({
    method: "PUT",
    body: payload,
  });

  return fetchData<MedicalRecordRequest>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    options
  );
}

export async function regenerateMedicalRecordRequestPdf(
  id: string
): Promise<{ pdfUrl: string | null }> {
  if (!id) {
    throw new Error("medical record request id is required");
  }

  const options = setHeaderOptions({ method: "POST" });
  return fetchData<{ pdfUrl: string | null }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}/regenerate-pdf`,
    options
  );
}

export async function uploadMedicalRecordRequestDocument(
  id: string,
  file: File
): Promise<{ pdfUrl: string | null; storageKey?: string | null }> {
  if (!id) throw new Error("medical record request id is required");
  if (!file) throw new Error("file is required");

  const formData = new FormData();
  formData.append("file", file);

  const options = await setFileUploadOptions({
    method: "POST",
    body: formData,
  });

  return fetchData<{ pdfUrl: string | null; storageKey?: string | null }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}/document`,
    options
  );
}
